import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import LotterySearchModal from '../../components/LotterySearchModal';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import ControlNumbersModal from '../../components/ControlNumbersModal';
import { listProductConfigs, listProducts } from '../../api/products';
import { listTirages } from '../../api/tirages';
import { resolveLotteryByCode } from '../../utils/lotteryLookup';
import {
  createNumberLimit, deleteNumberLimit, listNumberLimits, updateNumberLimit as updateNumberLimitApi,
} from '../../api/numberLimits';
import './NumberControlPage.css';

const scopeParams = (filter, scopeId) => {
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Superviseur' || filter === 'Banque') return { bank: scopeId };
  return {};
};

const NumberControlPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [lottery, setLottery] = useState('0');
  const [lotteryName, setLotteryName] = useState('TOUS');
  const [lotteryLookupError, setLotteryLookupError] = useState('');
  const [showLotteryModal, setShowLotteryModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  const isAllLotteries = String(lottery) === '0';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const scope = scopeParams(filter, scopeId);
    // Loterie "0" (TOUS) : pas de tirage 0 cote backend -> on charge toutes les
    // configs / limites de la portee (toutes loteries confondues) et on agrege.
    Promise.all([
      listProducts(),
      // TOUS = le parametre general de la portee (tirage vide, 2026-09-25) ;
      // loterie precise = valeur reellement appliquee (propre ou heritee).
      isAllLotteries
        ? listProductConfigs({ tirage: '0', ...scope })
        : listProductConfigs({ tirage: lottery, ...scope, effective: true }),
      isAllLotteries ? listNumberLimits({ ...scope }) : listNumberLimits({ tirage: lottery, ...scope }),
      isAllLotteries ? listTirages() : Promise.resolve(null),
    ])
      .then(([products, configs, numberLimits, tirages]) => {
        if (cancelled) return;

        // Valeur commune a une liste, sinon '' (= valeurs differentes selon la loterie).
        const agreed = (arr, getter) => {
          const vals = [...new Set(arr.map((x) => String(getter(x))))];
          return vals.length === 1 ? vals[0] : '';
        };

        if (isAllLotteries) {
          const totalLotteries = (tirages || []).length || 1;
          const cfgByProduct = {};
          configs.forEach((c) => { (cfgByProduct[c.product] ||= []).push(c); });
          const nlByProduct = {};
          numberLimits.forEach((nl) => { (nlByProduct[nl.product] ||= []).push(nl); });

          setGroups(products.map((p) => {
            const cfgs = cfgByProduct[p.id] || [];
            const byKey = {};
            (nlByProduct[p.id] || []).forEach((nl) => {
              (byKey[nl.numbers] ||= []).push(nl);
            });
            return {
              id: p.id,
              product: p.name,
              limit: cfgs.length ? agreed(cfgs, (c) => c.online_limit) : '0',
              localLimit: cfgs.length ? agreed(cfgs, (c) => c.local_limit) : '0',
              numbers: Object.entries(byKey).map(([k, list]) => ({
                rowKey: k,
                ids: list.map((nl) => nl.id),
                number: list[0].numbers,
                limit: agreed(list, (nl) => nl.online_limit),
                localLimit: agreed(list, (nl) => nl.local_limit ?? ''),
                lotteryCount: list.length,
                totalLotteries,
              })),
            };
          }));
          return;
        }

        const configByProduct = Object.fromEntries(configs.map((c) => [c.product, c]));
        const limitsByProduct = {};
        numberLimits.forEach((nl) => {
          (limitsByProduct[nl.product] ||= []).push(nl);
        });
        setGroups(products.map((p) => ({
          id: p.id,
          product: p.name,
          limit: configByProduct[p.id] ? configByProduct[p.id].online_limit : '0',
          localLimit: configByProduct[p.id] ? configByProduct[p.id].local_limit : '0',
          numbers: (limitsByProduct[p.id] || []).map((nl) => ({
            rowKey: String(nl.id), id: nl.id, number: nl.numbers,
            limit: nl.online_limit, localLimit: nl.local_limit ?? '',
          })),
        })));
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger les limites.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lottery, filter, scopeId, isAllLotteries, refreshTick]);

  const products = groups.map(g => g.product);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  // Quitter le champ Loterie : résout le code tapé -> nom affiché à côté (accepte "0" = TOUS).
  const handleLotteryBlur = async () => {
    const c = String(lottery).trim();
    if (c === '') { setLotteryLookupError(''); return; }
    const res = await resolveLotteryByCode(c);
    if (res) {
      setLottery(res.code);
      setLotteryName(res.name);
      setLotteryLookupError('');
    } else {
      setLotteryLookupError('Loterie introuvable');
    }
  };

  // En mode TOUS une ligne agrege plusieurs NumberLimit (une par loterie) -> `ids`.
  const rowIds = (num) => (num.ids || (num.id != null ? [num.id] : []));

  const updateNumberLimit = (groupId, rowKey, value) => {
    setGroups(prev => prev.map((g) => (
      g.id === groupId
        ? { ...g, numbers: g.numbers.map((n) => (n.rowKey === rowKey ? { ...n, limit: value } : n)) }
        : g
    )));
  };

  const persistNumberLimit = async (groupId, rowKey) => {
    const num = groups.find(g => g.id === groupId)?.numbers.find(n => n.rowKey === rowKey);
    if (!num) return;
    const ids = rowIds(num);
    if (ids.length === 0 || String(num.limit).trim() === '') return;
    try {
      await Promise.all(ids.map((id) => updateNumberLimitApi(id, { online_limit: num.limit })));
    } catch (err) {
      setError('Sauvegarde de la limite impossible.');
    }
  };

  const updateLocalLimit = (groupId, rowKey, value) => {
    setGroups(prev => prev.map((g) => (
      g.id === groupId
        ? { ...g, numbers: g.numbers.map((n) => (n.rowKey === rowKey ? { ...n, localLimit: value } : n)) }
        : g
    )));
  };

  // Limite locale optionnelle : contrairement a la limite en ligne, un champ vide
  // est une valeur valide (= efface l'override, on retombe sur ProductConfig.local_limit).
  const persistLocalLimit = async (groupId, rowKey) => {
    const num = groups.find(g => g.id === groupId)?.numbers.find(n => n.rowKey === rowKey);
    if (!num) return;
    const ids = rowIds(num);
    if (ids.length === 0) return;
    const value = String(num.localLimit).trim();
    try {
      await Promise.all(ids.map((id) => updateNumberLimitApi(id, { local_limit: value === '' ? null : value })));
    } catch (err) {
      setError('Sauvegarde de la limite locale impossible.');
    }
  };

  const handleDeleteNumber = async (groupId, rowKey) => {
    const num = groups.find(g => g.id === groupId)?.numbers.find(n => n.rowKey === rowKey);
    if (!num) return;
    try {
      await Promise.all(rowIds(num).map((id) => deleteNumberLimit(id)));
      setGroups(prev => prev.map((g) => (
        g.id === groupId ? { ...g, numbers: g.numbers.filter((n) => n.rowKey !== rowKey) } : g
      )));
    } catch (err) {
      setError('Suppression impossible.');
    }
  };

  const handleSaveNew = async ({ product, number, onlineLimit, localLimit }) => {
    const group = groups.find(g => g.product === product);
    if (!group) return;
    setError('');
    setNotice('');
    // On ne peut pas ajouter deux fois la limite d'un même numéro pour la même
    // loterie (demandé explicitement) — vérif locale immédiate, le serveur
    // revalide aussi (cf. NumberLimitSerializer, contraintes DB en filet de sécurité).
    if (lottery !== '0' && group.numbers.some(n => n.number === number)) {
      setError(`Une limite existe déjà pour le numéro « ${number} » sur cette loterie.`);
      return;
    }
    const scope = scopeParams(filter, scopeId);
    try {
      if (lottery === '0') {
        // Loterie "0" (TOUS) : on cree la limite pour chaque loterie du catalogue.
        // Certaines peuvent deja avoir ce numero -> on ignore ces echecs isoles et
        // on rapporte seulement le nombre reellement cree.
        const tirages = await listTirages();
        let ok = 0;
        for (const t of tirages) {
          try {
            await createNumberLimit({
              tirage: Number(t.code), product: group.id, ...scope,
              numbers: number, online_limit: onlineLimit, local_limit: localLimit || null,
            });
            ok += 1;
          } catch (_) { /* limite deja presente sur cette loterie */ }
        }
        setNotice(`Limite « ${number} » appliquee a ${ok}/${tirages.length} loteries.`);
        setRefreshTick((t) => t + 1);
        return;
      }
      const created = await createNumberLimit({
        tirage: Number(lottery),
        product: group.id,
        ...scope,
        numbers: number,
        online_limit: onlineLimit,
        local_limit: localLimit || null,
      });
      setGroups(prev => prev.map(g => (
        g.product === product
          ? {
            ...g,
            numbers: [...g.numbers, {
              rowKey: String(created.id), id: created.id, number: created.numbers,
              limit: created.online_limit, localLimit: created.local_limit ?? '',
            }],
          }
          : g
      )));
    } catch (err) {
      setError('Création de la limite impossible.');
    }
  };

  return (
    <Layout>
      <p className="nc-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/controles" className="bc-link">Controls</Link>
        {' >> '}
        <span className="bc-current">Number Control</span>
      </p>

      <div className="nc-card">
        <div className="nc-card-header">
          <span className="nc-card-title">Controle de Numeros</span>
        </div>
        <div className="nc-card-body">
          <div className="nc-radio-row">
            {['Tout', 'Groupe', 'Superviseur', 'Banque'].map((opt) => (
              <label key={opt} className="nc-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="nc-row nc-scope-row">
              <label className="nc-label">{filter}</label>
              {(filter === 'Banque' || filter === 'Superviseur') ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="nc-input nc-input-sm"
                  iconClass="nc-icon-btn"
                  nameClass="nc-copy-name"
                  errClass="nc-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="nc-input nc-input-sm"
                  iconClass="nc-icon-btn"
                  nameClass="nc-copy-name"
                  errClass="nc-lookup-err"
                />
              )}
            </div>
          )}

          <form onSubmit={handleSearch} className="nc-form">
            <div className="nc-row">
              <label className="nc-label">Loterie</label>
              <input
                className="nc-input nc-input-sm"
                type="text"
                value={lottery}
                onChange={e => { setLottery(e.target.value); setLotteryLookupError(''); }}
                onFocus={e => e.target.select()}
                onBlur={handleLotteryBlur}
              />
              <span className="nc-icon-btn" onClick={() => setShowLotteryModal(true)}>🔍</span>
              {lotteryLookupError
                ? <span className="nc-lookup-err">{lotteryLookupError}</span>
                : lotteryName && <span className="nc-copy-name">{lotteryName}</span>}
            </div>

            {showLotteryModal && (
              <LotterySearchModal
                onSelect={(code, name) => { setLottery(String(code)); setLotteryName(name || ''); setLotteryLookupError(''); }}
                onClose={() => setShowLotteryModal(false)}
              />
            )}

            <button type="submit" className="nc-search-btn">Chercher</button>
          </form>

          {error && <p className="nc-error">{error}</p>}
          {notice && <p className="nc-notice">{notice}</p>}
        </div>
      </div>

      <div className="nc-card nc-results">
        <div className="nc-card-header">
          <span className="nc-card-title">Controle de Numeros</span>
        </div>
        <div className="nc-card-body nc-table-body">
          {isAllLotteries && (
            <p className="nc-all-hint">
              Mode TOUS : numeros communs a toutes les loteries. (n/N) = nombre de loteries ayant ce numero.
              Une limite vide = valeurs differentes selon la loterie. Editer ou effacer agit sur toutes les
              loteries concernees.
            </p>
          )}
          {loading ? (
            <p>Chargement...</p>
          ) : (
          <table className="nc-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Produit</th>
                <th>Numero</th>
                <th>Limite Online</th>
                <th>Limite Local</th>
                <th>
                  <button type="button" className="nc-new-btn" onClick={() => setShowNewModal(true)}>Nouveau</button>
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => {
                const isBare = group.numbers.length === 0;
                return (
                <React.Fragment key={group.id}>
                  <tr className={isBare ? 'nc-group-row nc-group-bare' : 'nc-group-row nc-group-all'}>
                    <td>{group.id}</td>
                    <td className="nc-product-bold">{group.product}</td>
                    <td>Tout</td>
                    <td>
                      <span className="nc-limit-readonly" title="Limite en ligne par defaut du produit (ProductConfig), lecture seule">{group.limit}</span>
                    </td>
                    <td>
                      <span className="nc-limit-readonly" title="Limite locale par defaut du produit (ProductConfig), lecture seule">{group.localLimit}</span>
                    </td>
                    <td></td>
                  </tr>
                  {group.numbers.map((n) => (
                    <tr key={n.rowKey}>
                      <td></td>
                      <td className="nc-product-plain">{group.product}</td>
                      <td>
                        {n.number}
                        {isAllLotteries && (
                          <span className="nc-agg-badge" title="Loteries ayant ce numero">
                            {' '}({n.lotteryCount}/{n.totalLotteries})
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          className="nc-cell-input"
                          value={n.limit}
                          placeholder={isAllLotteries && n.limit === '' ? '≠' : undefined}
                          onChange={e => updateNumberLimit(group.id, n.rowKey, e.target.value)}
                          onBlur={() => persistNumberLimit(group.id, n.rowKey)}
                        />
                      </td>
                      <td>
                        <input
                          className="nc-cell-input"
                          value={n.localLimit}
                          placeholder="optionnel"
                          onChange={e => updateLocalLimit(group.id, n.rowKey, e.target.value)}
                          onBlur={() => persistLocalLimit(group.id, n.rowKey)}
                        />
                      </td>
                      <td>
                        <span className="nc-delete-link" onClick={() => handleDeleteNumber(group.id, n.rowKey)}>*Effacer</span>
                      </td>
                    </tr>
                  ))}
                  <tr className="nc-group-spacer"><td colSpan={6}></td></tr>
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      </div>

      {showNewModal && (
        <ControlNumbersModal
          lotteryLabel={`${lottery} - ${lotteryName}`}
          products={products}
          onSave={handleSaveNew}
          onClose={() => setShowNewModal(false)}
        />
      )}
    </Layout>
  );
};

export default NumberControlPage;
